import { useCallback, useEffect, useRef, useState } from 'react'
import tw, { TwStyle } from 'twin.macro'
import { motion } from 'framer-motion'
import { useScrollLock, useOnClickOutside } from 'usehooks-ts'

import { Portal } from '@headlessui/react'

type NavContainerProps = {
  className?: string
  css?: TwStyle | TwStyle[]
  /** The anchor position of the drawer on the page */
  position: 'left' | 'right'
  isDrawerOpen?: boolean
  /** Display only the drawer */
  isDrawerOnly?: boolean
  /** Breakpoint to hide the drawer */
  drawerBreakpoint?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  /** Header ref used to offset the position of the drawer */
  headerRef?: React.RefObject<HTMLElement>
  children: React.ReactNode
  onRequestClose?: () => void
  showOverlay?: boolean
  fullWidth?: boolean
}

export default function NavContainer({
  className,
  css,
  position = 'left',
  isDrawerOpen = false,
  isDrawerOnly = false,
  drawerBreakpoint = 'md',
  children,
  onRequestClose,
  headerRef,
  showOverlay = true,
  fullWidth = false,
}: NavContainerProps) {
  const ref = useRef<HTMLDivElement>(null)

  const [offset, setOffset] = useState(0)
  const calculateOffset = useCallback(() => {
    if (headerRef?.current) {
      const rect = headerRef.current.getBoundingClientRect()
      setOffset(rect.bottom)
    }
  }, [headerRef?.current])

  // Get the header height to set the offset for the drawer
  useEffect(() => {
    calculateOffset()
  }, [headerRef?.current])

  // Lock the body when the drawer is open
  const { lock, unlock } = useScrollLock({
    autoLock: false,
  })
  useEffect(() => {
    calculateOffset()
    isDrawerOpen ? lock() : unlock()
  }, [isDrawerOpen])

  useOnClickOutside(
    ref,
    useCallback(() => {
      onRequestClose?.()
    }, [])
  )

  return (
    <>
      {showOverlay && (
        <Portal>
          <motion.div
            tw="fixed opacity-0 inset-0 bg-black/60 md:(hidden)"
            css={[
              isDrawerOpen ? tw` z-10` : tw` -z-50`,
              drawerBreakpoint === 'sm' && tw`sm:(hidden)`,
              drawerBreakpoint === 'md' && tw`md:(hidden)`,
              drawerBreakpoint === 'lg' && tw`lg:(hidden)`,
              drawerBreakpoint === 'xl' && tw`xl:(hidden)`,
              drawerBreakpoint === '2xl' && tw`2xl:(hidden)`,
            ]}
            animate={{ opacity: isDrawerOpen ? 1 : 0 }}
            aria-hidden="true"
          />
        </Portal>
      )}
      <motion.div
        className={className}
        style={
          {
            '--nav-container-drawer-offset': `${offset}px`,
            '--nav-container-drawer-height': `calc(100vh - ${offset}px)`,
          } as any
        }
        css={[
          tw`fixed top-0 bottom-0 max-w-full bg-white z-20 h-full flex-none overflow-auto pb-12`,
          tw`top-[--nav-container-drawer-offset] h-[-nav-container-drawer-height]`,
          !isDrawerOnly && drawerBreakpoint === 'sm' && tw`sm:(block h-auto py-4 translate-x-0! top-auto)`,
          !isDrawerOnly && drawerBreakpoint === 'md' && tw`md:(block h-auto py-4 translate-x-0! top-auto)`,
          !isDrawerOnly && drawerBreakpoint === 'lg' && tw`lg:(block h-auto py-4 translate-x-0! top-auto)`,
          !isDrawerOnly && drawerBreakpoint === 'xl' && tw`xl:(block h-auto py-4 translate-x-0! top-auto)`,
          !isDrawerOnly && drawerBreakpoint === '2xl' && tw`2xl:(block h-auto py-4 translate-x-0! top-auto)`,
          position === 'left' && tw`left-0 right-auto -translate-x-full`,
          position === 'right' && tw`right-0 left-auto translate-x-full`,
          fullWidth ? tw`w-[100vw]` : tw`w-[20rem]`,
          css,
        ]}
        initial="closed"
        variants={{
          open: { translateX: 0 },
          closed: { translateX: position === 'left' ? '-100%' : '100%' },
        }}
        animate={isDrawerOpen ? 'open' : 'closed'}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        ref={ref}
      >
        {children}
      </motion.div>
    </>
  )
}
